import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Gamepad2,
  DollarSign,
  BarChart3,
  Target,
  RefreshCcw,
  Palette,
  TrendingUp,
  Clock,
  Coins
} from 'lucide-react';

const GamingHero = () => {
  return (
    <section className="py-20 md:py-32 grid-bg relative overflow-hidden" data-testid="gaming-hero">
      <div className="hero-glow -top-40 -left-40 opacity-40" />
      <div className="hero-glow bottom-0 right-0 opacity-30" style={{ background: 'radial-gradient(circle, rgba(112, 0, 255, 0.2) 0%, transparent 70%)' }} />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-purple/10 border border-neon-purple/30 text-neon-purple text-sm mb-6">
              <Gamepad2 className="w-4 h-4" />
              Gaming & App Specialists
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Why Gaming & App Clients <span className="text-neon-green">Love Us</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-xl leading-relaxed">
              We don't just run campaigns. We understand the unique economics and metrics that drive success in the gaming and app industry.
            </p>
          </div>
          
          <div className="relative">
            <div className="glass rounded-2xl overflow-hidden neon-border">
              <img 
                src="https://images.unsplash.com/photo-1632603093711-0d93a0bcc6cc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="High end gaming setup"
                className="w-full h-[400px] object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WeSpeak = () => {
  const metrics = [
    { icon: DollarSign, label: 'CPI Benchmarks', description: 'We know the real cost of acquisition across genres and geos.' },
    { icon: BarChart3, label: 'Retention Curves', description: 'D1, D7, D30 — we optimize for the metrics that matter.' },
    { icon: Coins, label: 'Ad Monetization Impact', description: 'Understanding how UA affects ad revenue and eCPM.' },
    { icon: Clock, label: 'UA Payback Windows', description: 'Calculating when your acquisition spend pays back.' },
    { icon: RefreshCcw, label: 'Creative Iteration Cycles', description: 'Rapid testing and scaling of winning creatives.' },
  ];

  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="we-speak-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Gamepad2 className="w-8 h-8 text-neon-green" />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-4">
            We Speak Your Metrics
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Our team has deep expertise in gaming and app performance metrics that drive real business outcomes.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric, index) => (
            <div 
              key={index}
              className="glass rounded-xl p-8 card-hover group"
              data-testid={`metric-card-${index}`}
            >
              <div className="w-14 h-14 rounded-lg bg-neon-green/10 flex items-center justify-center mb-6 group-hover:neon-border transition-all">
                <metric.icon className="w-7 h-7 text-neon-green" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">{metric.label}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{metric.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LTVMindset = () => {
  const benefits = [
    { icon: Target, label: 'Quality Over Quantity', description: 'We target users with high engagement potential, not just low CPI.' },
    { icon: TrendingUp, label: 'LTV Optimization', description: 'Every campaign is optimized for lifetime value, not just installs.' },
    { icon: BarChart3, label: 'Cohort Analysis', description: 'Deep dive into user cohorts to understand true ROAS over time.' },
    { icon: Coins, label: 'Monetization Focus', description: 'UA strategies aligned with your monetization model (IAP, ads, subscriptions).' },
  ];

  return (
    <section className="py-20 md:py-32 grid-bg relative" data-testid="ltv-mindset-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm mb-6">
              <DollarSign className="w-4 h-4" />
              Our Philosophy
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
              LTV {'>'} CPI Mindset
            </h2>
            <p className="text-2xl text-neon-green font-medium mb-4">
              We don't chase cheap installs.
            </p>
            <p className="text-2xl text-white font-heading mb-8">
              We acquire valuable users.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              Anyone can drive low-cost installs. We focus on acquiring users who engage, retain, and monetize — delivering true long-term value for your business.
            </p>
          </div>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="glass rounded-xl p-6 flex items-start gap-4 card-hover"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-neon-green/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-neon-green" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">{benefit.label}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const GamingCTA = () => {
  return (
    <section className="py-20 md:py-32 bg-surface relative overflow-hidden" data-testid="gaming-cta-section">
      <div className="hero-glow top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-40" />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <Gamepad2 className="w-16 h-16 text-neon-green mx-auto mb-8" />
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
          Ready to Level Up Your UA?
        </h2>
        <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
          Whether you're launching a new game or scaling an existing app, we're ready to help you acquire users that matter.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="gaming-cta-btn">
            Book a Free Growth Audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="gaming-services-btn">
            Explore Our Services
          </Link>
        </div>
      </div>
    </section>
  );
};

const Gaming = () => {
  return (
    <div data-testid="gaming-page">
      <GamingHero />
      <WeSpeak />
      <LTVMindset />
      <GamingCTA />
    </div>
  );
};

export default Gaming;
